import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { HardDrive, Upload } from "lucide-react";
import {
  api,
  apiBase,
  type StorageBucket,
  type StorageObjectMeta,
} from "@/lib/api";
import { storageSignedDownloadPath } from "@/lib/platformPaths";
import { EmptyState, ErrorBox, PageHeader, Spinner } from "@/components/ui";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ROUTES } from "@/routes";

export function StoragePage() {
  const { id = "" } = useParams<{ id: string }>();
  const [buckets, setBuckets] = useState<StorageBucket[]>([]);
  const [selected, setSelected] = useState<string>("");
  const [objects, setObjects] = useState<StorageObjectMeta[]>([]);
  const [prefix, setPrefix] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newBucket, setNewBucket] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [uploadPath, setUploadPath] = useState("");
  const [signedUrl, setSignedUrl] = useState<string | null>(null);

  const loadBuckets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.listStorageBuckets(id);
      setBuckets(data ?? []);
      if (!selected && data?.length) {
        setSelected(data[0].name);
      }
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [id, selected]);

  const loadObjects = useCallback(async () => {
    if (!id || !selected) {
      setObjects([]);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.listStorageObjects(
        id,
        selected,
        prefix.trim() || undefined,
      );
      setObjects(data ?? []);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }, [id, selected, prefix]);

  useEffect(() => {
    if (!id) return;
    void loadBuckets();
  }, [id, loadBuckets]);

  useEffect(() => {
    void loadObjects();
  }, [loadObjects]);

  async function createBucket() {
    const name = newBucket.trim().toLowerCase();
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      await api.createStorageBucket(id, { name, is_public: isPublic });
      setNewBucket("");
      setIsPublic(false);
      await loadBuckets();
      setSelected(name);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function onUpload(file: File | null) {
    if (!file || !selected) return;
    const path = (uploadPath.trim() || file.name).replace(/^\//, "");
    setBusy(true);
    setError(null);
    try {
      await api.uploadStorageObject(
        id,
        selected,
        path,
        file,
        file.type || "application/octet-stream",
      );
      setUploadPath("");
      await loadObjects();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function download(obj: StorageObjectMeta) {
    setBusy(true);
    setError(null);
    try {
      await api.downloadStorageObject(id, selected, obj.path, obj.path);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function sign(obj: StorageObjectMeta) {
    setBusy(true);
    setError(null);
    setSignedUrl(null);
    try {
      const { data } = await api.signStorageObject(id, selected, {
        path: obj.path,
        ttl_seconds: 3600,
      });
      const url = `${apiBase()}${storageSignedDownloadPath(data.token)}`;
      setSignedUrl(url);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function remove(obj: StorageObjectMeta) {
    if (!confirm(`Delete object ${obj.path}?`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteStorageObject(id, selected, obj.path);
      await loadObjects();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (loading && buckets.length === 0)
    return <Spinner label="Loading storage…" />;

  return (
    <div>
      <PageHeader
        eyebrow="Storage"
        title="Objects"
        description="Project buckets and objects (platform storage API). Signed download URLs are time-boxed. Web media lives under the Images tab."
        actions={
          <div className="flex items-center gap-2">
            <Link
              to={ROUTES.storageImages(id)}
              className="text-xs text-text-muted underline-offset-2 hover:underline"
            >
              Images
            </Link>
            <Badge variant="secondary" className="gap-1">
              <HardDrive className="h-3 w-3" strokeWidth={1.75} />
              {buckets.length} bucket{buckets.length !== 1 ? "s" : ""}
            </Badge>
          </div>
        }
      />

      <div className="mb-4 flex gap-2 border-b border-border pb-2">
        <span className="rounded-md bg-surface-container px-3 py-1.5 text-sm font-medium text-text-primary">
          Objects
        </span>
        <Link
          to={ROUTES.storageImages(id)}
          className="rounded-md px-3 py-1.5 text-sm text-text-muted hover:bg-surface-container"
        >
          Images
        </Link>
      </div>

      {error ? (
        <div className="mb-4">
          <ErrorBox error={error} />
        </div>
      ) : null}

      <div className="card mb-6 p-5">
        <h2 className="mb-3 font-sans text-sm font-semibold text-text-primary">
          Create bucket
        </h2>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-text-muted">
            Name
            <input
              className="input min-w-[12rem] text-sm"
              value={newBucket}
              onChange={(e) => setNewBucket(e.target.value)}
              placeholder="avatars"
              autoComplete="off"
            />
          </label>
          <label className="flex items-center gap-2 text-xs text-text-muted">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
            />
            Public bucket
          </label>
          <Button
            type="button"
            size="sm"
            disabled={busy || !newBucket.trim()}
            onClick={() => void createBucket()}
          >
            Create
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-text-muted">
          Bucket
          <select
            className="input text-sm"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
          >
            <option value="">Select…</option>
            {buckets.map((b) => (
              <option key={b.id} value={b.name}>
                {b.name}
                {b.is_public ? " (public)" : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-text-muted">
          Prefix
          <input
            className="input min-w-[10rem] text-sm"
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
            placeholder="optional/"
          />
        </label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!selected || busy}
          onClick={() => void loadObjects()}
        >
          Refresh objects
        </Button>
      </div>

      {selected ? (
        <div className="card mb-6 p-5">
          <h2 className="mb-3 flex items-center gap-2 font-sans text-sm font-semibold text-text-primary">
            <Upload className="h-4 w-4" strokeWidth={1.75} />
            Upload to {selected}
          </h2>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-xs text-text-muted">
              Object path
              <input
                className="input min-w-[14rem] text-sm"
                value={uploadPath}
                onChange={(e) => setUploadPath(e.target.value)}
                placeholder="folder/file.png"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-text-muted">
              File
              <input
                type="file"
                className="text-sm"
                onChange={(e) => void onUpload(e.target.files?.[0] ?? null)}
              />
            </label>
          </div>
        </div>
      ) : null}

      {signedUrl ? (
        <div className="card mb-4 border border-primary/30 p-4">
          <p className="mb-1 text-xs font-medium text-text-primary">
            Signed download URL (do not commit)
          </p>
          <code className="block break-all font-mono text-[11px] text-text-muted">
            {signedUrl}
          </code>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mt-2"
            onClick={() => {
              void navigator.clipboard.writeText(signedUrl);
            }}
          >
            Copy URL
          </Button>
        </div>
      ) : null}

      {!selected ? (
        <EmptyState title="No bucket selected">
          Create a bucket or pick one to list objects.
        </EmptyState>
      ) : busy && objects.length === 0 ? (
        <Spinner label="Loading objects…" />
      ) : objects.length === 0 ? (
        <EmptyState title="No objects">
          Upload a file or adjust the prefix filter.
        </EmptyState>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Path</th>
                <th>Type</th>
                <th>Size</th>
                <th>Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {objects.map((o) => (
                <tr key={o.id}>
                  <td className="font-mono text-xs">{o.path}</td>
                  <td className="text-text-muted text-xs">{o.content_type}</td>
                  <td className="tabular-nums text-xs">
                    {o.size_bytes.toLocaleString()} B
                  </td>
                  <td className="text-text-muted text-xs">
                    {new Date(o.updated_at).toLocaleString()}
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      <button
                        type="button"
                        className="btn-secondary !px-2 !py-1 text-xs"
                        onClick={() => void download(o)}
                      >
                        Download
                      </button>
                      <button
                        type="button"
                        className="btn-secondary !px-2 !py-1 text-xs"
                        onClick={() => void sign(o)}
                      >
                        Sign URL
                      </button>
                      <button
                        type="button"
                        className="btn-danger !px-2 !py-1 text-xs"
                        onClick={() => void remove(o)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
